import { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Check, X, ArrowRight, TrendingUp, Clock, Coins } from 'lucide-react';
import { cur } from '@/lib/currency';
import { Reveal } from './primitives';
import { heading, headingWhite } from './landingData';

/* ═══════════ ROI CALCULATOR ═══════════ */
export function ROICalculator({ onCTA }) {
  const [branches, setBranches] = useState(3);
  const [employees, setEmployees] = useState(25);
  const [adminHours, setAdminHours] = useState(20);

  const r = useMemo(() => {
    // Simple, defensible model: Smarto automates ~65% of manual admin/reporting
    // work and consolidates ~4 disparate tools.
    const hourlyCost = 12; // blended admin cost per hour (USD-ish, shown as SR-style)
    const hoursSavedMonthly = Math.round(adminHours * branches * 0.65 * 4.3);
    const laborSaved = hoursSavedMonthly * hourlyCost;
    const toolSaved = branches * 140; // replaced point tools per branch / mo
    const monthly = laborSaved + toolSaved;
    return {
      hoursSavedMonthly,
      monthly,
      yearly: monthly * 12,
    };
  }, [branches, adminHours]);

  const Slider = ({ label, value, setValue, min, max, step, suffix }) => (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-[13px] font-medium text-[#1D1D1F]">{label}</label>
        <span className="text-[13px] font-bold text-[#047857] tabular-nums">{value}{suffix}</span>
      </div>
      <input
        type="range" min={min} max={max} step={step} value={value}
        onChange={(e) => setValue(Number(e.target.value))}
        className="w-full accent-[#047857] cursor-pointer"
        data-testid={`roi-slider-${label.toLowerCase().replace(/[^a-z]/g, '-')}`}
      />
    </div>
  );

  const fmt = (n) => `${cur()} ` + n.toLocaleString();

  return (
    <section className="py-24 md:py-32 bg-[#F9F9F6]" id="roi">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <Reveal>
          <div className="text-center mb-14">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D97706] mb-3">See your impact</p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight" style={heading}>
              Calculate what Smarto saves you.
            </h2>
            <p className="text-[15px] text-[#86868B] mt-4 max-w-xl mx-auto">Adjust the sliders to your operation — see the time and money you reclaim every month.</p>
          </div>
        </Reveal>

        <div className="grid lg:grid-cols-2 gap-6 items-stretch">
          <Reveal>
            <div className="bg-white rounded-3xl border border-[#E8E8ED] p-8 space-y-7 h-full" data-testid="roi-calculator">
              <Slider label="Branches / locations" value={branches} setValue={setBranches} min={1} max={50} step={1} suffix="" />
              <Slider label="Employees" value={employees} setValue={setEmployees} min={1} max={500} step={1} suffix="" />
              <Slider label="Admin hours / week (per branch)" value={adminHours} setValue={setAdminHours} min={2} max={60} step={1} suffix=" hrs" />
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="relative overflow-hidden rounded-3xl bg-[#0B1120] p-8 h-full flex flex-col justify-center" data-testid="roi-results">
              <div className="absolute -top-20 -right-20 w-72 h-72 bg-[#047857]/20 blur-[100px] rounded-full" aria-hidden />
              <div className="relative z-10 space-y-6">
                <div>
                  <div className="flex items-center gap-2 text-[#34D399] text-[13px] font-medium"><Clock size={15} /> Time reclaimed</div>
                  <div className="text-3xl font-bold text-white mt-1 tabular-nums" data-testid="roi-hours">{r.hoursSavedMonthly.toLocaleString()} hrs<span className="text-base text-[#94A3B8] font-normal">/mo</span></div>
                </div>
                <div className="h-px bg-white/10" />
                <div>
                  <div className="flex items-center gap-2 text-[#34D399] text-[13px] font-medium"><Coins size={15} /> Estimated monthly savings</div>
                  <div className="text-4xl font-bold text-white mt-1 tabular-nums" data-testid="roi-monthly">{fmt(r.monthly)}</div>
                </div>
                <div>
                  <div className="flex items-center gap-2 text-[#D97706] text-[13px] font-medium"><TrendingUp size={15} /> Projected yearly savings</div>
                  <div className="text-2xl font-bold text-[#D97706] mt-1 tabular-nums" data-testid="roi-yearly">{fmt(r.yearly)}</div>
                </div>
                <button onClick={onCTA} className="mt-2 w-full bg-[#047857] hover:bg-[#065F46] text-white py-3.5 rounded-full text-[15px] font-semibold transition-all active:scale-[0.98] flex items-center justify-center gap-2" data-testid="roi-cta">
                  Start saving today <ArrowRight size={16} />
                </button>
                <p className="text-[11px] text-[#94A3B8]/70 text-center">Estimates based on typical multi-branch automation. Your results may vary.</p>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ═══════════ COMPARISON TABLE ═══════════ */
const COMPARISON_ROWS = [
  'One unified platform (POS, Accounting, HR, Inventory)',
  'Real-time multi-branch dashboards',
  'ZATCA Phase-2 e-invoicing built-in',
  'Multi-country tax & payroll compliance',
  'Automated WhatsApp / email reporting',
  'Loan & advance auto-deduction in payroll',
  'AI analytics & insights',
  'Single monthly price — no per-tool fees',
];

export function ComparisonTable() {
  const cols = [
    { key: 'smarto', label: 'Smarto', highlight: true },
    { key: 'spreadsheets', label: 'Spreadsheets' },
    { key: 'multi', label: 'Multiple Tools' },
  ];
  // which columns satisfy each row
  const matrix = [
    { smarto: true, spreadsheets: false, multi: 'partial' },
    { smarto: true, spreadsheets: false, multi: 'partial' },
    { smarto: true, spreadsheets: false, multi: false },
    { smarto: true, spreadsheets: false, multi: 'partial' },
    { smarto: true, spreadsheets: false, multi: false },
    { smarto: true, spreadsheets: false, multi: false },
    { smarto: true, spreadsheets: false, multi: 'partial' },
    { smarto: true, spreadsheets: 'partial', multi: false },
  ];

  const Cell = ({ v, highlight }) => {
    if (v === true) return <Check className={`w-5 h-5 mx-auto ${highlight ? 'text-[#047857]' : 'text-emerald-500'}`} strokeWidth={2.5} />;
    if (v === 'partial') return <span className="text-[11px] font-semibold text-[#D97706] uppercase">Partial</span>;
    return <X className="w-5 h-5 mx-auto text-[#CBD5E1]" strokeWidth={2.5} />;
  };

  return (
    <section className="py-24 md:py-32 bg-white">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <Reveal>
          <div className="text-center mb-14">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#047857] mb-3">Why Smarto</p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight" style={heading}>
              Everything in one place. Nothing left behind.
            </h2>
            <p className="text-[15px] text-[#86868B] mt-4 max-w-xl mx-auto">Stop stitching together spreadsheets and single-purpose apps. See how Smarto compares.</p>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="overflow-x-auto rounded-3xl border border-[#E8E8ED]" data-testid="comparison-table">
            <table className="w-full text-sm min-w-[560px]">
              <thead>
                <tr className="border-b border-[#E8E8ED]">
                  <th className="text-left px-5 py-4 font-semibold text-[#86868B] text-[13px]">Capability</th>
                  {cols.map(c => (
                    <th key={c.key} className={`px-4 py-4 text-center text-[13px] font-bold ${c.highlight ? 'text-[#047857] bg-[#ECFDF5]' : 'text-[#1D1D1F]'}`}>
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {COMPARISON_ROWS.map((row, i) => (
                  <motion.tr
                    key={i}
                    initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: i * 0.04 }}
                    className="border-b border-[#F0F0F2] last:border-0"
                    data-testid={`comparison-row-${i}`}
                  >
                    <td className="px-5 py-3.5 text-[#1D1D1F] font-medium">{row}</td>
                    {cols.map(c => (
                      <td key={c.key} className={`px-4 py-3.5 text-center ${c.highlight ? 'bg-[#ECFDF5]' : ''}`}>
                        <Cell v={matrix[i][c.key]} highlight={c.highlight} />
                      </td>
                    ))}
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
